import { CallState } from "@b2bportal/core-types";
import { createSelector } from "@reduxjs/toolkit";
import type { AccountState } from "./slice";

export const getPointsBalance = createSelector(
  (state: AccountState) => state,
  (state) => state.account?.pointsBalance
);

export const getPointsBalanceData = createSelector(
  getPointsBalance,
  (pointsBalance) => pointsBalance?.resource?.data
);

export const getHasPoints = createSelector(
  getPointsBalanceData,
  (pointsBalanceData) => pointsBalanceData?.totalPoints.value > 0
);

export const getIsFixedPointsPricingEligible = (state: AccountState) =>
  state.account.isFixedPointsPricingEligible;

/**
 * Corporate (commercial) RBC card product codes. Cardholders on these products
 * get corporate-specific copy and support contacts (see RBC-2329).
 */
export const CORPORATE_CARD_PRODUCT_CODES = ["CAV", "BAV", "BNA"] as const;

export const getCardProductCode = createSelector(
  getPointsBalanceData,
  (pointsBalanceData) => pointsBalanceData?.cardProductCode
);

export const getIsCorporateCardProduct = createSelector(
  getCardProductCode,
  (cardProductCode) =>
    cardProductCode != null &&
    (CORPORATE_CARD_PRODUCT_CODES as readonly string[]).includes(
      cardProductCode
    )
);

export const getRewardsAccountId = createSelector(
  getPointsBalance,
  (pointsBalance) => pointsBalance?.resource?.data?.rewardsAccountId
);

export const isRewardsLoading = createSelector(
  getPointsBalance,
  (pointsBalance) =>
    pointsBalance?.resource?.state === CallState.NotCalled ||
    pointsBalance?.resource?.state === CallState.InProcess
);

export const getRewardsError = createSelector(
  getPointsBalance,
  (pointsBalance) => pointsBalance?.resource?.state === CallState.Failed
);

export const getBasePointsToCashMultiplier = createSelector(
  getPointsBalanceData,
  (pointsBalanceData) => pointsBalanceData?.basePointsToCashMultiplier
);

export const getGridOverageAndTaxesPointsToCashMultiplier = createSelector(
  getPointsBalanceData,
  (pointsBalanceData) =>
    pointsBalanceData?.gridOverageAndTaxesPointsToCashMultiplier
);

/**
 * Whether the signed-in account is an Emerald cardholder. Emerald accounts
 * redeem grid overage, taxes/fees and flight add-ons (seats, CFAR) at a
 * separate rate from their base flex rate, and the backend only returns
 * `gridOverageAndTaxesPointsToCashMultiplier` for those accounts.
 */
export const getIsEmeraldAccount = createSelector(
  getGridOverageAndTaxesPointsToCashMultiplier,
  (gridOverageAndTaxesPointsToCashMultiplier) =>
    gridOverageAndTaxesPointsToCashMultiplier !== undefined
);

export const getHasMinimumRedemptionPoints = createSelector(
  getPointsBalanceData,
  (pointsBalanceData) =>
    pointsBalanceData &&
    pointsBalanceData.totalPoints.value >=
      (pointsBalanceData.minimumRedemptionPoints ?? 0)
);

export const getMinimumRedemptionPoints = createSelector(
  getPointsBalanceData,
  (pointsBalanceData) => pointsBalanceData?.minimumRedemptionPoints
);
